import moment from 'moment';

import { notify } from 'src/clients/ApiService';
import { VariableContractField } from 'src/entities/Contract';
import { useShareContract } from 'src/hooks/useShareContract';
import { useAppDispatch, useAppSelector } from 'src/hooks/useStore';
import { FormModal } from 'src/legacy/components/Modals';
import {
  ApiTags,
  appAPI,
  ContractRecipient,
  ContractTemplate,
  FieldType,
  generateErrorMessageForShareContract,
  InputType,
  ShareContractTemplateInput,
  useShareContractTemplateMutation,
} from 'src/services/api';
import { RootState } from 'src/store';
import { getProfileFromUser, UserProfile } from 'src/utils/UserUtils';

type ShareContractModalProps = {
  open: boolean;
  selectedTemplate: ContractTemplate | undefined;
  onClose: () => void;
};

function isVariableInput(
  field: ContractTemplate['fields'][0],
): field is VariableContractField {
  return field.inputType === InputType.Variable;
}

export const ShareContractModal = ({
  open,
  selectedTemplate,
  onClose,
}: ShareContractModalProps) => {
  const [shareContractTemplate] = useShareContractTemplateMutation();
  const dispatch = useAppDispatch();
  const clients = useAppSelector((state: RootState) => state.clients.clients);

  const variableInputs = selectedTemplate?.fields?.filter(isVariableInput);

  const initialFormValue = {
    recipientIds: [],
    fields: variableInputs
      ? variableInputs.reduce<Record<string, string>>(
          (curr, acc) => ({
            ...curr,
            [acc.id]:
              acc.type === FieldType.Date ? moment().format('MM/DD/YYYY') : '',
          }),
          {},
        )
      : {},
  };

  /**
   * Shares the template with selected clients.
   * @param values - Form values containing recipient IDs and input field values
   */
  const handleShareContract = async (values: {
    recipientIds: ContractRecipient[];
    fields: Record<string, string>;
  }) => {
    if (!selectedTemplate?.id) return;

    const { recipientIds } = values;

    const queries = recipientIds.map((recipient) => {
      const requestBody: ShareContractTemplateInput = {
        clientId: recipient.clientId,
        companyId: recipient.companyId,
        contractTemplateId: selectedTemplate.id,
        creationMode: 'template',
      };

      // Add variable input values in the request body only when there are variable inputs
      if (values.fields && Object.keys(values.fields).length > 0) {
        requestBody.variableValues = values.fields;
      }
      return shareContractTemplate(requestBody)
        .unwrap()
        .catch((e) => ({
          error: e,
          recipient,
        }));
    });

    const results = await Promise.all(queries);

    // Filter the results to find requests that resulted in errors
    const erroredRequests = results.filter((r) => 'error' in r);

    // If there are failed requests, identify the clients associated with these failed requests
    if (erroredRequests.length > 0) {
      const failedRequestClients: UserProfile[] = erroredRequests
        .map((request) => {
          const client = clients.find(
            (c) =>
              'recipient' in request && c.id === request.recipient.clientId,
          );
          if (!client) return null;
          return getProfileFromUser(client);
        })
        .filter((c): c is UserProfile => c !== null);

      // Notify with an error status and generate an error message based on failed request clients
      notify({
        status: 'error',
        errorMessage:
          generateErrorMessageForShareContract(failedRequestClients),
        dispatch,
      });
      return;
    }

    // invalidate "GET: /contracts-template" to fetch the updated data
    dispatch(
      appAPI.util.invalidateTags([
        ApiTags.contract_templates,
        ApiTags.contracts,
      ]),
    );
    notify({
      status: 'success',
      successMessage: 'Contract template shared successfully.',
      dispatch,
    });
  };

  return (
    <FormModal
      initialFormValue={initialFormValue}
      useFormHook={() =>
        useShareContract({
          variableInputs: variableInputs || [],
          selectedTemplateRef: selectedTemplate?.ref || '',
        })
      }
      open={open}
      title="Share contract"
      onClose={onClose}
      buttonLabel="Send contract"
      cancelButtonLabel="Dismiss"
      handleSave={handleShareContract}
    />
  );
};
